import { batch } from 'react-redux';
import { identity } from 'fp-ts/function';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { ReservationFilterParameters, ReservationResult } from '@mews-commander/data-access';
import { RequireFields } from '@mews/std';
import { createFetchAction, mapId, notEmpty, callApiInChunks } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import prefix from '../prefix';
import { setReservationGroups } from '../ducks/reservationGroups';
import { setReservations } from '../ducks/reservations';
import { setCompanionships } from '../ducks/companionships';
import { setOrderItems, deleteOrderItems, getOrderItemIdsByServiceOrderIds } from '../ducks/orderItems';
import { setCustomers } from '../ducks/customers';
import { setServiceOrderNotes } from '../ducks/serviceOrderNotes';
import { setBusinessSegments } from '../ducks/businessSegments';
import { setAvailabilityBlocks } from '../ducks/availabilityBlocks';
import { setRates } from '../ducks/rates';
import { setVouchers } from '../ducks/vouchers';
import { setTravelAgencies } from '../ducks/travelAgencies';
import { setProducts } from '../ducks/products';
import { setTravelAgencyContracts } from '../ducks/travelAgencyContracts';
import { setCompanies } from '../ducks/companies';
import {
    resetReservationPurchaseOrderNumbers,
    setReservationPurchaseOrderNumbers,
} from '../ducks/reservationPurchaseOrderNumber';

const RESERVATIONS_FETCH = prefix('RESERVATIONS_FETCH');
const RESERVATIONS_UPDATE = prefix('RESERVATIONS_UPDATE');

const fetch =
    (params: ReservationFilterParameters): CommanderThunkAction<AsyncAction<ReservationResult>> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.reservations.getAll(params).then((data) => {
            const {
                reservations,
                reservationGroups,
                companionships,
                orderItems,
                accounts,
                notes,
                businessSegments,
                availabilityBlocks,
                rates,
                companyPartners,
                vouchers,
                travelAgencyCompanies,
                products,
                travelAgencyContracts,
                purchaseOrderNumbers,
            } = data;

            batch(() => {
                if (reservations && reservations.length > 0) dispatch(setReservations(reservations));

                if (reservationGroups && reservationGroups.length > 0) {
                    dispatch(setReservationGroups(reservationGroups));
                }

                if (purchaseOrderNumbers) {
                    if (purchaseOrderNumbers.length === 0) {
                        dispatch(resetReservationPurchaseOrderNumbers());
                    } else {
                        dispatch(setReservationPurchaseOrderNumbers(purchaseOrderNumbers));
                    }
                }

                if (companionships && companionships.length > 0) {
                    dispatch(setCompanionships(companionships));
                }

                if (orderItems && orderItems.length > 0) {
                    const reservationIds = mapId(reservations);
                    const existingOrderItemIds = getOrderItemIdsByServiceOrderIds(getState(), {
                        serviceOrderIds: reservationIds,
                    });
                    dispatch(deleteOrderItems(existingOrderItemIds));

                    // @ts-expect-error
                    dispatch(setOrderItems(orderItems));
                }

                if (accounts && accounts.length > 0) {
                    dispatch(setCustomers(accounts));
                }

                if (notes && notes.length > 0) {
                    // @ts-expect-error
                    dispatch(setServiceOrderNotes(notes));
                }

                if (businessSegments && businessSegments.length > 0) {
                    dispatch(setBusinessSegments(businessSegments));
                }

                if (availabilityBlocks && availabilityBlocks.length > 0) {
                    dispatch(setAvailabilityBlocks(availabilityBlocks));
                }
                if (rates && rates.length > 0) {
                    // @ts-expect-error $TSFixMeError: Expected 2 arguments, but got 1.
                    dispatch(setRates(rates));
                }
                if (companyPartners && companyPartners.length > 0) {
                    dispatch(setCompanies(companyPartners));
                }
                if (vouchers && vouchers.length > 0) {
                    dispatch(setVouchers(vouchers));
                }
                if (travelAgencyCompanies && travelAgencyCompanies.length > 0) {
                    dispatch(setTravelAgencies(travelAgencyCompanies));
                }
                if (products && products.length > 0) {
                    // @ts-expect-error
                    dispatch(setProducts(products));
                }
                if (travelAgencyContracts && travelAgencyContracts.length > 0) {
                    dispatch(setTravelAgencyContracts(travelAgencyContracts));
                }
            });

            return data;
        });

        return dispatch(createFetchAction(RESERVATIONS_FETCH, promise));
    };

export const fetchCurrentEnterpriseReservationsByAccountIds =
    ({ accountIds, timeFilter, startUtc, endUtc, states, extent }: $TSFixMe) =>
    (dispatch: $TSFixMe) => {
        const currentEnterpriseId = getCurrentEnterpriseId();

        return dispatch(
            fetch({ accountIds, timeFilter, startUtc, endUtc, states, extent, enterpriseId: currentEnterpriseId })
        );
    };

interface ReservationNonNullableResult {
    value: RequireFields<ReservationResult, keyof ReservationResult>;
}

export const fetchCurrentEnterpriseReservationsByReservationIds =
    ({
        reservationIds,
        timeFilter,
        startUtc,
        endUtc,
        states,
        extent,
    }: $TSFixMe): CommanderThunkAction<Promise<ReservationNonNullableResult>> =>
    async (dispatch) => {
        const currentEnterpriseId = getCurrentEnterpriseId();

        const result = await callApiInChunks({
            apiCall: async (ids: string[]) =>
                dispatch(
                    fetch({
                        reservationIds: ids,
                        timeFilter,
                        startUtc,
                        endUtc,
                        states,
                        extent,
                        enterpriseId: currentEnterpriseId,
                    })
                ),
            chunkSize: 1000,
            processChunks: identity<string[][]>,
        })(reservationIds);

        return {
            value: {
                reservations: result.flatMap(({ value }) => value.reservations ?? []),
                reservationGroups: result.flatMap(({ value }) => value.reservationGroups ?? []),
                companionships: result.flatMap(({ value }) => value.companionships ?? []),
                orderItems: result.flatMap(({ value }) => value.orderItems ?? []),
                accounts: result.flatMap(({ value }) => value.accounts ?? []),
                notes: result.flatMap(({ value }) => value.notes ?? []),
                businessSegments: result.flatMap(({ value }) => value.businessSegments ?? []),
                availabilityBlocks: result.flatMap(({ value }) => value.availabilityBlocks ?? []),
                companyPartners: result.flatMap(({ value }) => value.companyPartners ?? []),
                vouchers: result.flatMap(({ value }) => value.vouchers ?? []),
                travelAgencyCompanies: result.flatMap(({ value }) => value.travelAgencyCompanies ?? []),
                products: result.flatMap(({ value }) => value.products ?? []),
                travelAgencyContracts: result.flatMap(({ value }) => value.travelAgencyContracts ?? []),
                purchaseOrderNumbers: result.flatMap(({ value }) => value.purchaseOrderNumbers ?? []),
            },
        } as ReservationNonNullableResult;
    };

export const fetchCurrentEnterpriseReservationsByGroupIds =
    ({ groupIds, timeFilter, startUtc, endUtc, states, extent }: $TSFixMe) =>
    (dispatch: $TSFixMe) => {
        const currentEnterpriseId = getCurrentEnterpriseId();

        return dispatch(
            fetch({
                groupIds,
                timeFilter,
                startUtc,
                endUtc,
                states,
                extent,
                enterpriseId: currentEnterpriseId,
            })
        );
    };

export const updateCurrentEnterpriseReservations =
    ({ reservationUpdates }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const enterpriseId = getCurrentEnterpriseId();

        const promise = container()
            .reservationComponent.update({ reservationUpdates, enterpriseId })
            .then(({ reservations }: $TSFixMe) => {
                if (notEmpty(reservations)) {
                    dispatch(setReservations(reservations));
                }
            });

        return dispatch(createFetchAction(RESERVATIONS_UPDATE, promise));
    };

export const assignReservationSlot =
    ({ reservationId }: $TSFixMe) =>
    (dispatch: $TSFixMe, _getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const promise = container().reservationComponent.assignSlot(reservationId);
        return dispatch(createFetchAction(RESERVATIONS_UPDATE, promise));
    };

export const updateReservationResource =
    ({
        reservationId,
        resourceId,
        updateBlockAvailability = false,
    }: {
        reservationId: string;
        resourceId: string;
        updateBlockAvailability: boolean;
    }): CommanderThunkAction<AsyncAction> =>
    async (dispatch, _getState, { container }) => {
        const promise = container().reservationComponent.updateResource(
            reservationId,
            resourceId,
            false,
            updateBlockAvailability
        );
        return dispatch(createFetchAction(RESERVATIONS_UPDATE, promise));
    };

export const fetchReservationsByAvailabilityBlockIds = ({
    availabilityBlockIds,
    enterpriseId,
    extent,
}: Pick<ReservationFilterParameters, 'availabilityBlockIds' | 'extent' | 'enterpriseId'>) =>
    fetch({ availabilityBlockIds, enterpriseId, extent });
